import { apiClient } from '../client'
import type {
  Accessibility,
  AccessibilityGroup,
  AccessibilityGroupWrite,
  AccessibilityListParams,
  AccessibilityWrite,
  ClearCacheResult,
  FeatureListParams,
  InvoiceListParams,
  PageParams,
  PaymentListParams,
  Plan,
  PlanCategory,
  PlanCategoryWrite,
  PlanFeature,
  PlanFeatureWrite,
  PlanFormOptions,
  PlanListParams,
  PlanWrite,
  PlatformInvoice,
  PlatformListParams,
  PlatformOrganization,
  PlatformPage,
  PlatformPayment,
  PlatformSetting,
  PlatformSubscription,
  PlatformTwoFactorMode,
  PlatformTwoFactorModeSaved,
  PlatformUser,
  PlatformUserListParams,
  PlatformUserTwoFactorDetail,
  PlatformUserTwoFactorPatch,
  SettingChoice,
  SettingChoiceListParams,
  SettingChoiceWrite,
  SettingFormOptions,
  SettingHistoryEntry,
  SettingListParams,
  SettingWrite,
  SubscriptionCreate,
  SubscriptionListParams,
  SubscriptionOperationResult,
  SubscriptionPatch,
  TwoFactorMode,
} from '../../app/modules/types/platform'

/**
 * The superuser console's API. Every route is refused with 403 for a
 * non-superuser, and the pages render that themselves ("Superusers only"), so
 * these calls are deliberately plain: errors are read by `readPlatformError`.
 */
export const PLATFORM_BASE = '/platform'
export const PLATFORM_MAX_PAGE_SIZE = 100

/** `a=1&b=2` without the empty values — an unset filter must not reach the server as `a=`. */
export function platformQuery(params?: PlatformListParams): string {
  if (!params) return ''
  const usp = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue
    usp.set(key, String(value))
  }
  return usp.toString()
}

export function platformUrl(path: string, params?: PlatformListParams): string {
  const qs = platformQuery(params)
  return qs ? `${PLATFORM_BASE}/${path}?${qs}` : `${PLATFORM_BASE}/${path}`
}

async function list<T>(path: string, params?: PlatformListParams): Promise<PlatformPage<T>> {
  const { data } = await apiClient.get<PlatformPage<T>>(platformUrl(path, params))
  return data
}
async function get<T>(path: string): Promise<T> {
  const { data } = await apiClient.get<T>(platformUrl(path))
  return data
}
async function post<T>(path: string, body?: unknown): Promise<T> {
  const { data } = await apiClient.post<T>(platformUrl(path), body)
  return data
}
async function patch<T>(path: string, body: unknown): Promise<T> {
  const { data } = await apiClient.patch<T>(platformUrl(path), body)
  return data
}
async function put<T>(path: string, body: unknown): Promise<T> {
  const { data } = await apiClient.put<T>(platformUrl(path), body)
  return data
}
async function remove(path: string): Promise<void> {
  await apiClient.delete(platformUrl(path))
}

/**
 * A feature's body. A new image goes up as multipart; without one the JSON body
 * is sent, and `image: null` stays in it — that is how a stored image is cleared.
 */
export function featureBody(payload: Partial<PlanFeatureWrite>): FormData | Record<string, unknown> {
  const entries = Object.entries(payload).filter(([, value]) => value !== undefined)
  if (!(payload.image instanceof File)) return Object.fromEntries(entries)
  const form = new FormData()
  for (const [key, value] of entries) {
    if (value === null) continue
    form.append(key, value instanceof File ? value : String(value))
  }
  return form
}
const featureRequest = (body: FormData | Record<string, unknown>) =>
  body instanceof FormData ? { headers: { 'Content-Type': 'multipart/form-data' } } : undefined

export const platformPlansApi = {
  list: (params?: PlanListParams) => list<Plan>('plans/', params),
  get: (id: string) => get<Plan>(`plans/${id}/`),
  create: (payload: PlanWrite) => post<Plan>('plans/', payload),
  update: (id: string, payload: Partial<PlanWrite>) => patch<Plan>(`plans/${id}/`, payload),
  remove: (id: string) => remove(`plans/${id}/`),
  formOptions: () => get<PlanFormOptions>('plans/form-options/'),

  listCategories: (params?: PageParams) => list<PlanCategory>('plan-categories/', params),
  createCategory: (payload: PlanCategoryWrite) => post<PlanCategory>('plan-categories/', payload),
  updateCategory: (id: string, payload: Partial<PlanCategoryWrite>) =>
    patch<PlanCategory>(`plan-categories/${id}/`, payload),
  removeCategory: (id: string) => remove(`plan-categories/${id}/`),

  listFeatures: (params?: FeatureListParams) => list<PlanFeature>('features/', params),
  createFeature: async (payload: PlanFeatureWrite): Promise<PlanFeature> => {
    const body = featureBody(payload)
    const { data } = await apiClient.post<PlanFeature>(platformUrl('features/'), body, featureRequest(body))
    return data
  },
  updateFeature: async (id: string, payload: Partial<PlanFeatureWrite>): Promise<PlanFeature> => {
    const body = featureBody(payload)
    const { data } = await apiClient.patch<PlanFeature>(platformUrl(`features/${id}/`), body, featureRequest(body))
    return data
  },
  removeFeature: (id: string) => remove(`features/${id}/`),

  listAccessibilityGroups: (params?: PageParams) => list<AccessibilityGroup>('accessibility-groups/', params),
  createAccessibilityGroup: (payload: AccessibilityGroupWrite) =>
    post<AccessibilityGroup>('accessibility-groups/', payload),
  updateAccessibilityGroup: (id: string, payload: Partial<AccessibilityGroupWrite>) =>
    patch<AccessibilityGroup>(`accessibility-groups/${id}/`, payload),
  removeAccessibilityGroup: (id: string) => remove(`accessibility-groups/${id}/`),
  listAccessibilities: (params?: AccessibilityListParams) => list<Accessibility>('accessibilities/', params),
  createAccessibility: (payload: AccessibilityWrite) => post<Accessibility>('accessibilities/', payload),
  updateAccessibility: (id: string, payload: Partial<AccessibilityWrite>) =>
    patch<Accessibility>(`accessibilities/${id}/`, payload),
  removeAccessibility: (id: string) => remove(`accessibilities/${id}/`),
}

export const platformSubscriptionsApi = {
  list: (params?: SubscriptionListParams) => list<PlatformSubscription>('subscriptions/', params),
  get: (id: string) => get<PlatformSubscription>(`subscriptions/${id}/`),
  create: (payload: SubscriptionCreate) => post<SubscriptionOperationResult>('subscriptions/', payload),
  update: (id: string, payload: SubscriptionPatch) =>
    patch<SubscriptionOperationResult>(`subscriptions/${id}/`, payload),
  changePlan: (id: string, plan: string) =>
    post<SubscriptionOperationResult>(`subscriptions/${id}/change-plan/`, { plan }),
  cancel: (id: string) => post<SubscriptionOperationResult>(`subscriptions/${id}/cancel/`),
}

export const platformOrganizationsApi = {
  list: (params?: PageParams) => list<PlatformOrganization>('organizations/', params),
}

export const platformBillingApi = {
  listInvoices: (params?: InvoiceListParams) => list<PlatformInvoice>('invoices/', params),
  getInvoice: (id: string) => get<PlatformInvoice>(`invoices/${id}/`),
  listPayments: (params?: PaymentListParams) => list<PlatformPayment>('payments/', params),
  getPayment: (id: string) => get<PlatformPayment>(`payments/${id}/`),
}

export const platformSettingsApi = {
  list: (params?: SettingListParams) => list<PlatformSetting>('settings/', params),
  get: (id: number) => get<PlatformSetting>(`settings/${id}/`),
  create: (payload: SettingWrite) => post<PlatformSetting>('settings/', payload),
  update: (id: number, payload: SettingWrite) => patch<PlatformSetting>(`settings/${id}/`, payload),
  remove: (id: number) => remove(`settings/${id}/`),
  history: (id: number, params?: PageParams) => list<SettingHistoryEntry>(`settings/${id}/history/`, params),
  clearCache: (ids?: number[]) => post<ClearCacheResult>('settings/clear-cache/', ids ? { ids } : {}),
  formOptions: () => get<SettingFormOptions>('settings/form-options/'),
  listChoices: (params?: SettingChoiceListParams) => list<SettingChoice>('setting-choices/', params),
  createChoice: (payload: SettingChoiceWrite) => post<SettingChoice>('setting-choices/', payload),
  updateChoice: (id: number, payload: Partial<SettingChoiceWrite>) =>
    patch<SettingChoice>(`setting-choices/${id}/`, payload),
  removeChoice: (id: number) => remove(`setting-choices/${id}/`),
}

export const platformTwoFactorApi = {
  getMode: () => get<PlatformTwoFactorMode>('two-factor/'),
  setMode: (mode: TwoFactorMode) => put<PlatformTwoFactorModeSaved>('two-factor/', { mode }),
  listUsers: (params?: PlatformUserListParams) => list<PlatformUser>('users/', params),
  getUser: (id: string) => get<PlatformUserTwoFactorDetail>(`users/${id}/two-factor/`),
  patchUser: (id: string, payload: PlatformUserTwoFactorPatch) =>
    patch<PlatformUserTwoFactorDetail>(`users/${id}/two-factor/`, payload),
  resetUser: async (id: string): Promise<void> => {
    await post<void>(`users/${id}/two-factor/reset/`)
  },
}
